/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { useContext } from "react";
import {
  Search,
  Trash2,
  Shield,
  Settings2,
  User,
  Phone,
  MapPin,
  Calendar,
  AlertCircle,
} from "lucide-react";
import Pagination from "../../../components/Pagination";
import { AdminContext } from "../../../context/AdminContext";
import {
  ADMIN_ACCESS,
  ADMIN_DEPARTMENTS,
  ADMIN_MODULE_LABELS,
  ALL_ADMIN_ACCESS,
  getDepartmentAccess,
  normalizeAdminAccess,
} from "../../../utils/adminAccess";

const ASSIGNABLE_ADMIN_ACCESS = ALL_ADMIN_ACCESS.filter(
  (access) => access !== ADMIN_ACCESS.SERVICE_CATEGORIES,
);

const SubAdminList = () => {
  const { subAdmins, fetchSubAdmins, deleteSubAdmin, updateSubAdminStatus } =
    useContext(AdminContext);
  const [admins, setAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [accessDraft, setAccessDraft] = useState({
    adminDepartment: "custom",
    adminAccess: [],
  });

  useEffect(() => {
    fetchSubAdmins();
  }, [fetchSubAdmins]);

  useEffect(() => {
    const mappedAdmins = subAdmins.map((admin) => ({
      id: admin.id,
      name: admin.name,
      email: admin.email,
      role: admin.isSuperAdmin ? "Super Admin" : "Sub Admin",
      avatar:
        admin.image ||
        `https://ui-avatars.com/api/?name=${encodeURIComponent(
          admin.name,
        )}&background=random&size=100`,
      phone: admin.phoneNumber || "N/A",
      address: admin.address
        ? `${admin.address.line1 || ""}, ${admin.address.line2 || ""}`.trim() ||
          "N/A"
        : "N/A",
      gender: admin.gender || "N/A",
      birthdate: admin.dob || "N/A",
      status: admin.status === "active" ? "Active" : "Inactive",
      rawStatus: admin.status || "active",
      adminDepartment: admin.adminDepartment || "custom",
      adminAccess: normalizeAdminAccess(admin.adminAccess || []),
    }));
    setAdmins(mappedAdmins);
  }, [subAdmins]);

  // Filter Logic
  const filteredAdmins = admins.filter(
    (admin) =>
      admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.email.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const paginatedAdmins = filteredAdmins.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const totalPages = Math.ceil(filteredAdmins.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  // Handlers
  const handleToggleStatus = (admin) => {
    setSelectedAdmin(admin);
    setShowStatusModal(true);
  };

  const confirmStatusChange = () => {
    if (selectedAdmin) {
      const newStatus =
        selectedAdmin.status === "Active" ? "deactivated" : "active";
      updateSubAdminStatus(selectedAdmin.id, newStatus);
      setShowStatusModal(false);
      setSelectedAdmin(null);
    }
  };

  const handleDelete = (admin) => {
    setSelectedAdmin(admin);
    setShowDeleteModal(true);
  };

  const handleEditAccess = (admin) => {
    setSelectedAdmin(admin);
    setAccessDraft({
      adminDepartment: admin.adminDepartment || "custom",
      adminAccess: normalizeAdminAccess(admin.adminAccess || []),
    });
    setShowAccessModal(true);
  };

  const handleAccessDepartmentChange = (e) => {
    const adminDepartment = e.target.value;
    setAccessDraft((prev) => ({
      ...prev,
      adminDepartment,
      adminAccess:
        adminDepartment === "custom"
          ? prev.adminAccess
          : getDepartmentAccess(adminDepartment),
    }));
  };

  const toggleAccessDraft = (access) => {
    setAccessDraft((prev) => {
      const hasAccess = prev.adminAccess.includes(access);
      return {
        ...prev,
        adminDepartment: "custom",
        adminAccess: normalizeAdminAccess(
          hasAccess
            ? prev.adminAccess.filter((item) => item !== access)
            : [...prev.adminAccess, access],
        ),
      };
    });
  };

  const confirmAccessChange = () => {
    if (selectedAdmin) {
      updateSubAdminStatus(
        selectedAdmin.id,
        selectedAdmin.rawStatus,
        accessDraft.adminAccess,
        accessDraft.adminDepartment,
      );
      setShowAccessModal(false);
      setSelectedAdmin(null);
    }
  };

  const confirmDelete = () => {
    if (selectedAdmin) {
      deleteSubAdmin(selectedAdmin.id);
      setShowDeleteModal(false);
      setSelectedAdmin(null);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text">
            Sub-Admin List
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1">
            Manage administrative accounts and permissions.
          </p>
        </div>
        <div className="flex gap-4">
          <div className="bg-card dark:bg-dark-surface p-3 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border flex items-center gap-3">
            <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-xl text-purple-600">
              <Shield size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-dark-muted">
                Total Admins
              </p>
              <p className="text-lg font-bold text-text dark:text-dark-text">
                {admins.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
        {/* Toolbar */}
        <div className="p-3 sm:p-4 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row justify-between gap-4 items-center bg-gray-50/50 dark:bg-gray-900/30">
          <div className="relative flex-1 w-full sm:max-w-md">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={16}
            />
            <input
              type="text"
              placeholder="Search admin by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-dark-border outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text text-sm transition-all"
            />
          </div>
        </div>

        {/* Table - Horizontal scroll on small screens */}
        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-sm text-left min-w-[1000px]">
            <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
              <tr>
                <th className="px-4 sm:px-6 py-3">Profile</th>
                <th className="px-4 sm:px-6 py-3">Role</th>
                <th className="px-4 sm:px-6 py-3">Access</th>
                <th className="px-4 sm:px-6 py-3">Contact Info</th>
                <th className="px-4 sm:px-6 py-3">Personal Info</th>
                <th className="px-4 sm:px-6 py-3 text-center">Status</th>
                <th className="px-4 sm:px-6 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {paginatedAdmins.map((admin) => (
                <tr
                  key={admin.id}
                  className="hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <td className="px-4 sm:px-6 py-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={admin.avatar}
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                        alt={admin.name}
                      />
                      <div>
                        <p className="font-bold text-text dark:text-dark-text text-sm sm:text-base">
                          {admin.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-dark-muted">
                          {admin.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-3">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-bold border ${
                        admin.role === "Super Admin"
                          ? "bg-red-50 text-primary border-red-100 dark:bg-primary dark:text-white dark:border-primary"
                          : "bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800"
                      }`}
                    >
                      {admin.role}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-3">
                    <div className="max-w-[220px]">
                      <p className="text-xs font-semibold text-text dark:text-dark-text">
                        {ADMIN_DEPARTMENTS[admin.adminDepartment]?.label ||
                          "Custom"}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-dark-muted line-clamp-2">
                        {admin.adminAccess.length
                          ? admin.adminAccess
                              .map((access) => ADMIN_MODULE_LABELS[access])
                              .join(", ")
                          : "No module access"}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-3">
                    <div className="flex flex-col text-xs text-gray-500 dark:text-dark-muted gap-1">
                      <span className="flex items-center gap-1.5">
                        <Phone size={12} />
                        <span className="truncate max-w-[120px] sm:max-w-none">
                          {admin.phone}
                        </span>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <MapPin size={12} />
                        <span className="truncate max-w-[120px] sm:max-w-none">
                          {admin.address}
                        </span>
                      </span>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-3">
                    <div className="flex flex-col text-xs text-gray-500 dark:text-dark-muted gap-1">
                      <span className="flex items-center gap-1.5">
                        <User size={12} /> {admin.gender}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Calendar size={12} /> {admin.birthdate}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-3 text-center">
                    <button
                      onClick={() => handleToggleStatus(admin)}
                      disabled={admin.role === "Super Admin"}
                      className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
                        admin.status === "Active"
                          ? "bg-emerald-500"
                          : "bg-gray-300 dark:bg-gray-600"
                      } ${
                        admin.role === "Super Admin"
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <span
                        className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-200 ${
                          admin.status === "Active"
                            ? "translate-x-6"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                    <div className="text-[10px] font-medium text-gray-500 dark:text-dark-muted mt-1">
                      {admin.status}
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-3 text-center">
                    <div className="flex justify-center gap-1">
                      <button
                        onClick={() => handleEditAccess(admin)}
                        disabled={admin.role === "Super Admin"}
                        className={`p-2 rounded-lg transition-colors ${
                          admin.role === "Super Admin"
                            ? "text-gray-300 cursor-not-allowed"
                            : "text-gray-400 hover:text-primary hover:bg-primary/10"
                        }`}
                      >
                        <Settings2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(admin)}
                        disabled={admin.role === "Super Admin"}
                        className={`p-2 rounded-lg transition-colors ${
                          admin.role === "Super Admin"
                            ? "text-gray-300 cursor-not-allowed"
                            : "text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        }`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {paginatedAdmins.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No admins found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-dark-surface p-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>
      </div>

      {/* Status Confirmation Modal */}
      {showStatusModal && selectedAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200 p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mx-auto mb-4 text-yellow-600 dark:text-yellow-400">
              <AlertCircle size={24} />
            </div>
            <h3 className="text-xl font-bold text-text dark:text-dark-text mb-2">
              Change Status?
            </h3>
            <p className="text-gray-500 dark:text-dark-muted mb-6 text-sm">
              Are you sure you want to{" "}
              {selectedAdmin.status === "Active" ? "deactivate" : "activate"}{" "}
              <span className="font-bold text-text dark:text-dark-text">
                {selectedAdmin.name}
              </span>
              ?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-text dark:text-dark-text rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Access Modal */}
      {showAccessModal && selectedAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200 p-6">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h3 className="text-xl font-bold text-text dark:text-dark-text">
                  Edit Access
                </h3>
                <p className="text-sm text-gray-500 dark:text-dark-muted mt-1">
                  {selectedAdmin.name}
                </p>
              </div>
              <Shield className="text-primary" size={24} />
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-text dark:text-dark-text mb-2">
                  Department Preset
                </label>
                <select
                  value={accessDraft.adminDepartment}
                  onChange={handleAccessDepartmentChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-gray-900 text-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  {Object.entries(ADMIN_DEPARTMENTS).map(([key, department]) => (
                    <option key={key} value={key}>
                      {department.label}
                    </option>
                  ))}
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-text dark:text-dark-text mb-2">
                  Dashboard Access
                </label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {ASSIGNABLE_ADMIN_ACCESS.map((access) => (
                    <label
                      key={access}
                      className={`flex items-center gap-3 rounded-xl border p-3 text-sm font-medium transition-colors ${
                        accessDraft.adminAccess.includes(access)
                          ? "border-primary bg-primary/5 text-primary"
                          : "border-gray-200 dark:border-dark-border text-gray-600 dark:text-dark-muted"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={accessDraft.adminAccess.includes(access)}
                        onChange={() => toggleAccessDraft(access)}
                        className="h-4 w-4 accent-primary"
                      />
                      <span>{ADMIN_MODULE_LABELS[access]}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAccessModal(false)}
                className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-800 text-text dark:text-dark-text rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmAccessChange}
                disabled={accessDraft.adminAccess.length === 0}
                className="flex-1 px-4 py-3 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold"
              >
                Save Access
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200 p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4 text-red-600 dark:text-red-400">
              <Trash2 size={24} />
            </div>
            <h3 className="text-xl font-bold text-text dark:text-dark-text mb-2">
              Delete Account?
            </h3>
            <p className="text-gray-500 dark:text-dark-muted mb-6 text-sm">
              Are you sure you want to delete{" "}
              <span className="font-bold text-text dark:text-dark-text">
                {selectedAdmin.name}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-text dark:text-dark-text rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubAdminList;
